/**
 * Shared primitives for entity workspaces (org page, provider page).
 *
 * The redesign treats "drill into an entity" as one pattern: a passport band
 * (identity + copyable chips + actions), a grouped nav rail, and an overview
 * of stat tiles + a health row. These are its atoms.
 */

import { useState } from 'react';
import { Check, Circle, ContentCopy } from '@mui/icons-material';
import { Tooltip, Typography } from '@mui/material';

import type { HealthItem } from './types';

/** One copyable chip: short label, mono value (IDs truncated), copy feedback. */
export function IdChip({
  label,
  value,
  truncate = true,
  compact = false,
}: {
  label: string;
  value: string;
  /** IDs truncate to stay chip-sized; human-readable values (emails) should not. */
  truncate?: boolean;
  /** Label-only chip for dense rows: the full value lives in the tooltip. */
  compact?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const copy = async (e: React.MouseEvent) => {
    // Chips sit inside clickable rows — copying must not also open the row.
    e.stopPropagation();
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };
  const shown =
    truncate && value.length > 22 ? `${value.slice(0, 10)}…${value.slice(-8)}` : value;
  return (
    <Tooltip title={copied ? 'Copied' : compact ? value : `Copy ${value}`}>
      <button
        type="button"
        onClick={copy}
        className="group flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs hover:border-gray-300 hover:bg-gray-50"
      >
        <span className="font-medium uppercase tracking-wide text-gray-400">{label}</span>
        {!compact && <span className="font-mono text-gray-700">{shown}</span>}
        {copied ? (
          <Check sx={{ fontSize: 14 }} className="text-green-600" />
        ) : (
          <ContentCopy
            sx={{ fontSize: 13 }}
            className="text-gray-300 group-hover:text-gray-500"
          />
        )}
      </button>
    </Tooltip>
  );
}

export function StatTile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="flex-1 border-l border-gray-200 py-1 pl-4 first:border-l-0 first:pl-0">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-400">{label}</div>
      <div className="mt-0.5 text-xl font-semibold text-gray-900">{value}</div>
      {sub && <div className="text-xs text-gray-500">{sub}</div>}
    </div>
  );
}

const DOT_COLOR: Record<HealthItem['status'], string> = {
  ok: 'text-green-500',
  warn: 'text-amber-500',
  off: 'text-gray-300',
};

export function HealthRow({ items }: { items: HealthItem[] }) {
  return (
    <div className="flex flex-wrap gap-x-5 gap-y-1.5">
      {items.map((item) => (
        <div key={item.label} className="flex items-center gap-1.5" title={item.detail}>
          <Circle sx={{ fontSize: 9 }} className={DOT_COLOR[item.status]} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {item.label}
          </Typography>
        </div>
      ))}
    </div>
  );
}

export function formatCentsShort(cents: number | null): string {
  if (cents == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    cents / 100,
  );
}
